import {
  getLocalPrintingPreferences,
  getPrintingStatus,
  printPdf,
} from '@/services/printing/directPrintService.js';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const GUIAS_BASE_URL = `${API_BASE_URL}/api/guias`;

const resolveHeaders = () => {
  const headers = {};

  try {
    const token = localStorage.getItem('token');
    const tenantRaw = localStorage.getItem('tenant');

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (tenantRaw) {
      try {
        const tenant = JSON.parse(tenantRaw);
        headers['X-Tenant'] = tenant?.code || tenant?.domain || tenantRaw;
      } catch {
        headers['X-Tenant'] = tenantRaw;
      }
    }
  } catch {
    // noop
  }

  return headers;
};

const buildGuiaPdfUrl = (guiaId, { download = false } = {}) =>
  `${GUIAS_BASE_URL}/${guiaId}/pdf${download ? '?download=1' : ''}`;

const fetchGuiaPdfBlob = async (guiaId, { download = false } = {}) => {
  const response = await fetch(buildGuiaPdfUrl(guiaId, { download }), {
    method: 'GET',
    headers: resolveHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    let message = 'No se pudo generar el PDF de la guía.';
    try {
      const payload = await response.json();
      message = payload?.error || payload?.message || message;
    } catch {
      // noop
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);

  return {
    blob,
    filename: filenameMatch?.[1] || `guia-${guiaId}.pdf`,
  };
};

const openWaitingWindow = () => {
  // `noopener`/`noreferrer` make some browsers return null even when the
  // popup was opened successfully. We need the reference briefly to load the
  // PDF, so detach the opener after creating the window instead.
  const popup = window.open('', '_blank');
  if (!popup) {
    throw new Error('El navegador bloqueó la apertura de la vista previa de la guía.');
  }

  try {
    popup.opener = null;
  } catch {
    // El navegador puede impedir modificar opener; la vista seguirá siendo segura.
  }

  popup.document.write(`
    <!doctype html>
    <html lang="es">
      <head>
        <meta charset="utf-8" />
        <title>Guía PDF</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; display: grid; place-items: center; min-height: 100vh; color: #1f2937; }
        </style>
      </head>
      <body>Cargando guía...</body>
    </html>
  `);
  popup.document.close();

  return popup;
};

const openBlobPreview = ({ blob, targetWindow }) => {
  const objectUrl = URL.createObjectURL(blob);
  targetWindow.location.replace(objectUrl);
  const revoke = () => window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  targetWindow.addEventListener('beforeunload', revoke, { once: true });
  return objectUrl;
};

const browserPrintStrategy = async (guiaId) => {
  const popup = openWaitingWindow();
  try {
    const pdf = await fetchGuiaPdfBlob(guiaId);
    const objectUrl = URL.createObjectURL(pdf.blob);
    popup.document.open();
    popup.document.write(`
      <!doctype html>
      <html lang="es">
        <head>
          <meta charset="utf-8" />
          <title>${pdf.filename}</title>
          <style>
            html, body { margin: 0; height: 100%; background: #111827; }
            iframe { border: 0; width: 100%; height: 100%; }
          </style>
        </head>
        <body>
          <iframe id="guia-pdf-frame" src="${objectUrl}" title="${pdf.filename}"></iframe>
          <script>
            const frame = document.getElementById('guia-pdf-frame');
            const printFrame = () => {
              try {
                if (frame.contentWindow) {
                  frame.contentWindow.focus();
                  frame.contentWindow.print();
                  return;
                }
              } catch (error) {
                console.warn('No se pudo imprimir el iframe PDF, fallback a window.print()', error);
              }
              window.print();
            };
            frame.addEventListener('load', () => setTimeout(printFrame, 350), { once: true });
            window.addEventListener('beforeunload', () => {
              try { URL.revokeObjectURL('${objectUrl}'); } catch (error) {}
            }, { once: true });
          </script>
        </body>
      </html>
    `);
    popup.document.close();
  } catch (error) {
    popup.close();
    throw error;
  }
};

const silentPrintStrategy = async (guiaId) => {
  const enabled = import.meta.env.VITE_GUIAS_SILENT_PRINT === '1';
  if (!enabled) {
    return browserPrintStrategy(guiaId);
  }

  throw new Error('Silent print aún no está implementado en entorno web.');
};

export async function previewGuiaPdf(guiaId) {
  const popup = openWaitingWindow();
  try {
    const pdf = await fetchGuiaPdfBlob(guiaId);
    openBlobPreview({ blob: pdf.blob, targetWindow: popup });
  } catch (error) {
    popup.close();
    throw error;
  }
}

export async function printGuiaPdf(guiaId, { strategy = 'browser' } = {}) {
  if (strategy === 'silent') {
    return silentPrintStrategy(guiaId);
  }
  return browserPrintStrategy(guiaId);
}

/**
 * Queues a guide PDF for the Sinamon Print Agent of this workstation.  A result
 * (rather than an exception) is returned for the expected first-run cases so callers
 * can take the operator to the setup wizard without presenting a technical error.
 */
export async function printGuiaDirectly(guiaId, scope = {}) {
  const status = await getPrintingStatus(scope);
  if (status.state !== 'connected') return { status: 'setup_required', reason: 'connector_unavailable' };

  const { printers, defaultPrinter, workstation } = status;
  const preferences = getLocalPrintingPreferences(scope);
  const savedPrinter = preferences.guidePrinter;
  if (savedPrinter && !printers.includes(savedPrinter)) {
    return { status: 'setup_required', reason: 'guide_printer_unavailable' };
  }
  const printerName = savedPrinter || (defaultPrinter && printers.includes(defaultPrinter) ? defaultPrinter : '');

  if (!printerName) return { status: 'setup_required', reason: 'guide_printer_unavailable' };

  const pdf = await fetchGuiaPdfBlob(guiaId);
  await printPdf({ deviceId: workstation.deviceId, printerName }, pdf.blob);
  return { status: 'printed', printerName, filename: pdf.filename };
}

export async function downloadGuiaPdf(guiaId) {
  const pdf = await fetchGuiaPdfBlob(guiaId, { download: true });
  const objectUrl = URL.createObjectURL(pdf.blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = pdf.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

export { buildGuiaPdfUrl, fetchGuiaPdfBlob };
